import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { sessionLogger } from "@/utils/sessionLogger";

/**
 * Mounts global listeners that feed the session logger.
 * Should be rendered exactly once, inside <BrowserRouter>.
 */
const SessionLoggerInit = () => {
  const location = useLocation();
  const initialized = useRef(false);

  // One-time global patches
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    // Environment snapshot
    sessionLogger.info("session", "Session started", {
      ua: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      lang: navigator.language,
      url: window.location.href,
    });

    // --- Patch console.error / console.warn ---
    const origError = console.error;
    const origWarn = console.warn;
    console.error = (...args: unknown[]) => {
      try {
        sessionLogger.error("console", args.map(stringifyArg).join(" "));
      } catch {
        // ignore
      }
      origError.apply(console, args as []);
    };
    console.warn = (...args: unknown[]) => {
      try {
        sessionLogger.warn("console", args.map(stringifyArg).join(" "));
      } catch {
        // ignore
      }
      origWarn.apply(console, args as []);
    };

    // --- window.onerror ---
    const onError = (
      msg: string | Event,
      source?: string,
      lineno?: number,
      colno?: number,
      error?: Error
    ) => {
      sessionLogger.error("window.error", typeof msg === "string" ? msg : "Error event", {
        source,
        lineno,
        colno,
        stack: error?.stack?.slice(0, 500),
      });
    };
    window.addEventListener("error", (e) =>
      onError(e.message, e.filename, e.lineno, e.colno, e.error)
    );

    // --- unhandledrejection ---
    const onRejection = (e: PromiseRejectionEvent) => {
      const reason = e.reason;
      sessionLogger.error(
        "promise.rejection",
        reason instanceof Error ? reason.message : String(reason),
        { stack: reason instanceof Error ? reason.stack?.slice(0, 500) : undefined }
      );
    };
    window.addEventListener("unhandledrejection", onRejection);

    // --- Patch fetch ---
    const origFetch = window.fetch.bind(window);
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
      const method = init?.method || (typeof input !== "string" && !(input instanceof URL) ? input.method : "GET");
      const start = performance.now();
      try {
        const res = await origFetch(input, init);
        const dur = Math.round(performance.now() - start);
        if (!res.ok) {
          sessionLogger.warn("fetch", `${method} ${shortUrl(url)} → ${res.status}`, {
            status: res.status,
            durMs: dur,
          });
        }
        return res;
      } catch (err) {
        const dur = Math.round(performance.now() - start);
        sessionLogger.error("fetch", `${method} ${shortUrl(url)} failed`, {
          error: err instanceof Error ? err.message : String(err),
          durMs: dur,
        });
        throw err;
      }
    };

    // --- Click delegation: capture button/link labels (no input values) ---
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const el = target.closest("button, a, [role='button']") as HTMLElement | null;
      if (!el) return;
      // Skip the export-log button itself to avoid noise
      if (el.dataset.sessionLoggerIgnore === "true") return;
      const label =
        el.getAttribute("aria-label") ||
        (el.textContent || "").trim().slice(0, 80) ||
        el.getAttribute("title") ||
        "(no label)";
      const tag = el.tagName.toLowerCase();
      const href = el.getAttribute("href") || undefined;
      sessionLogger.info("click", `${tag}: ${label}`, href ? { href: shortUrl(href) } : undefined);
    };
    document.addEventListener("click", onClick, true);

    return () => {
      console.error = origError;
      console.warn = origWarn;
      window.fetch = origFetch;
      window.removeEventListener("unhandledrejection", onRejection);
      document.removeEventListener("click", onClick, true);
    };
  }, []);

  // Track route changes
  useEffect(() => {
    sessionLogger.info("route", `Navigated to ${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  return null;
};

function stringifyArg(a: unknown): string {
  if (a instanceof Error) return a.message;
  if (typeof a === "string") return a;
  try {
    return JSON.stringify(a);
  } catch {
    return String(a);
  }
}

function shortUrl(url: string): string {
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname + (u.search ? "?" + u.search.slice(0, 80) : "");
  } catch {
    return url.slice(0, 120);
  }
}

export default SessionLoggerInit;
